import { TableId, Venue, WeeklyOpeningHours } from "@greeter/core";
import {
  adjustForMidnightPeriod,
  DateOnly,
  DatePeriod,
  toISODateString,
} from "@greeter/date";
import { IVenueApi } from "@greeter/venue-api";
import {
  createFetchMachine,
  FetchActorFields,
  unwrapData,
} from "@greeter/xstate-util";
import { setup, raise, assign } from "xstate";
import { bookingPeriods } from "../BookingOverviewPage";
import { getServiceDayPeriod } from "@greeter/util";

export type TableOverviewApiRequirements = Pick<
  IVenueApi,
  | "fetchLockedTablesByPeriod"
  | "fetchBookedTablesByPeriod"
  | "fetchVenue"
  | "lockTables"
  | "unlockTables"
  | "fetchFloorPlan"
  | "fetchSpecialOpeningHours"
>;

type TableOverviewContext = {
  bookedTables: TableId[];
  lockedTables: TableId[];
  selectedTables: TableId[];
  selectedLockedTables: TableId[];

  area?: string;
  period?: DatePeriod;

  date?: Date;
  bookableDates: Map<string, DatePeriod>;

  api: FetchActorFields<TableOverviewApiRequirements>;
};

type SetLockedTablesEvent = { type: "setLockedTables"; data: TableId[] };
type SetBookedTablesEvent = { type: "setBookedTables"; data: TableId[] };
type ClearSelectedTablesEvent = { type: "clearSelectedTables" };
type SyncTablesEvent = { type: "syncTables" };
type SelectTableEvent = { type: "selectTable"; data: TableId };
type ValidateEvent = { type: "validate" };

type TableOverviewEvents =
  | ValidateEvent
  | SelectTableEvent
  | SetLockedTablesEvent
  | SetBookedTablesEvent
  | ClearSelectedTablesEvent
  | SyncTablesEvent
  | { type: "unlockTables" }
  | { type: "lockTables" }
  | { type: "setArea"; data: string }
  | { type: "setDate"; data: Date }
  | { type: "setPeriod"; data: DatePeriod }
  | { type: "setBookableDates"; data: Map<string, DatePeriod> };

type TableOverviewMachineInput = {
  api: TableOverviewApiRequirements;
};

function eq(a: TableId, b: TableId) {
  return a.area === b.area && a.tableNumber === b.tableNumber;
}

export const TableOverviewMachine = setup({
  types: {} as {
    context: TableOverviewContext;
    events: TableOverviewEvents;
    input: TableOverviewMachineInput;
  },
}).createMachine({
  id: "table-overview",
  context({ input, spawn, self }) {
    const fetchLockedTablesByPeriodRef = spawn(
      createFetchMachine(input.api.fetchLockedTablesByPeriod.bind(input.api)),
    );
    fetchLockedTablesByPeriodRef.on("fetched", (d) => {
      self.send({ type: "setLockedTables", data: d.data.map((e) => e.table) });
    });
    const fetchBookedTablesByPeriodRef = spawn(
      createFetchMachine(input.api.fetchBookedTablesByPeriod.bind(input.api)),
    );
    fetchBookedTablesByPeriodRef.on("fetched", (d) => {
      self.send({ type: "setBookedTables", data: d.data });
    });
    const unlockTablesRef = spawn(
      createFetchMachine(input.api.unlockTables.bind(input.api)),
      { syncSnapshot: true },
    );
    unlockTablesRef.on("fetched", () => {
      const period = self.getSnapshot().context.period;
      if (period)
        fetchLockedTablesByPeriodRef.send({ type: "fetch", data: [period] });
    });
    unlockTablesRef.on("error", console.error);
    const lockTablesRef = spawn(
      createFetchMachine(input.api.lockTables.bind(input.api)),
      { syncSnapshot: true },
    );
    lockTablesRef.on("fetched", () => {
      const period = self.getSnapshot().context.period;
      if (period)
        fetchLockedTablesByPeriodRef.send({ type: "fetch", data: [period] });
    });
    lockTablesRef.on("error", console.error);
    const fetchVenueRef = spawn(
      createFetchMachine(input.api.fetchVenue.bind(input.api)),
    );
    fetchVenueRef.send({ type: "fetch", data: [] });

    const fetchSpecialOpeningHoursRef = spawn(
      createFetchMachine(input.api.fetchSpecialOpeningHours.bind(input.api)),
    );
    fetchSpecialOpeningHoursRef.send({ type: "fetch", data: [] });

    const fetchFloorPlanRef = spawn(
      createFetchMachine(input.api.fetchFloorPlan.bind(input.api)),
    );
    fetchFloorPlanRef.send({ type: "fetch", data: [] });

    Promise.all([
      new Promise((r) => {
        fetchSpecialOpeningHoursRef.on("fetched", r);
      }),
      new Promise((r) => fetchVenueRef.on("fetched", r)),
    ]).then(() => {
      const now = new Date();
      const periods = Venue.createDatePeriodsFromOpeningHours({
        range: 365,
        now: now,
        specialOpeningHours:
          fetchSpecialOpeningHoursRef.getSnapshot().context.data ?? [],
        openingHours:
          fetchVenueRef.getSnapshot().context.data?.bookableOpeningHours ??
          new WeeklyOpeningHours(),
      });

      const m = new Map<string, DatePeriod>();

      for (const period of periods) {
        m.set(
          toISODateString(adjustForMidnightPeriod(period.from)),
          period.toStrict(),
        );
      }
      self.send({ type: "setBookableDates", data: m });
      const d = adjustForMidnightPeriod(periods[0].from);
      self.send({
        type: "setDate",
        data: d,
      });
      // const p = m.get(toISODateString(d));
      const serviceDayPeriod = getServiceDayPeriod({
        bookingArrivalTime: d,
      }).toStrict();
      self.send({
        type: "setPeriod",
        data: serviceDayPeriod,
      });
    });

    return {
      lockedTables: [],
      bookedTables: [],
      selectedTables: [],
      selectedLockedTables: [],
      bookableDates: new Map(),
      date: new Date(),
      api: {
        fetchBookedTablesByPeriodRef,
        fetchLockedTablesByPeriodRef,
        fetchFloorPlanRef,
        fetchSpecialOpeningHoursRef,
        lockTablesRef,
        unlockTablesRef,
        fetchVenueRef,
      },
    };
  },
  type: "parallel",
  on: {
    setBookableDates: {
      actions: assign({ bookableDates: unwrapData }),
    },
    setDate: {
      actions: [
        assign({ date: unwrapData }),
        assign({
          period: ({ context: { bookableDates }, event }) =>
            getServiceDayPeriod({
              bookingArrivalTime: bookableDates.get(toISODateString(event.data))
                ?.from!,
            }).toStrict(),
        }),
        ({
          context: {
            period,
            api: { fetchLockedTablesByPeriodRef, fetchBookedTablesByPeriodRef },
          },
        }) => {
          if (!period) return;
          fetchLockedTablesByPeriodRef.send({ type: "fetch", data: [period] });
          fetchBookedTablesByPeriodRef.send({ type: "fetch", data: [period] });
        },
      ],
    },
    setArea: {
      actions: assign({ area: unwrapData }),
    },
    setPeriod: {
      actions: assign({ period: unwrapData }),
    },
    setBookedTables: {
      actions: [
        assign(({ context: _, event: ev }) => {
          return { bookedTables: ev.data };
        }),
        raise({ type: "validate" }),
      ],
    },
    setLockedTables: {
      actions: [
        assign(({ context: _, event: ev }) => {
          return { lockedTables: ev.data };
        }),
        raise({ type: "validate" }),
      ],
    },
    clearSelectedTables: {
      actions: [
        assign(() => {
          return {
            selectedTables: [] as TableId[],
            selectedLockedTables: [] as TableId[],
          };
        }),
      ],
    },
    unlockTables: {
      actions: [
        assign(
          ({
            context: {
              selectedLockedTables,
              lockedTables,
              period,
              api: { unlockTablesRef },
            },
          }) => {
            if (!period) return {};
            unlockTablesRef.send({
              type: "fetch",
              data: [
                {
                  type: "dateperiod",
                  period: period,
                  tables: selectedLockedTables,
                },
              ],
            });
            return {
              lockedTables: lockedTables.filter(
                (lt) => !selectedLockedTables.some((slt) => eq(lt, slt)),
              ),
            };
          },
        ),
        raise({ type: "clearSelectedTables" }),
      ],
    },
    lockTables: {
      actions: [
        assign(
          ({
            context: {
              selectedTables,
              lockedTables,
              period,
              api: { lockTablesRef },
            },
          }) => {
            if (!period) return {};
            lockTablesRef.send({
              type: "fetch",
              data: [
                {
                  type: "dateperiod",
                  period: period,
                  tables: selectedTables,
                },
              ],
            });
            return { lockedTables: lockedTables.concat(selectedTables) };
          },
        ),
        raise({ type: "clearSelectedTables" }),
      ],
    },
    syncTables: {
      actions: [
        assign(
          ({
            context: { selectedTables, selectedLockedTables, lockedTables },
          }) => {
            const moveSelectedTables = selectedTables.filter((st) =>
              lockedTables.some((lt) => eq(st, lt)),
            );

            const moveSelectedLockedTables = selectedLockedTables.filter(
              (st) => !lockedTables.some((lt) => eq(st, lt)),
            );

            return {
              selectedTables: [...moveSelectedLockedTables, ...selectedTables],
              selectedLockedTables: [
                ...moveSelectedTables,
                ...selectedLockedTables,
              ],
            };
          },
        ),
      ],
    },
  },
  states: {
    ticker: {
      initial: "idle",
      states: {
        idle: {
          after: {
            5_000: { target: "tick" },
          },
        },
        tick: {
          after: {
            0: {
              target: "idle",
              actions: ({
                context: {
                  api: {
                    fetchLockedTablesByPeriodRef,
                    fetchBookedTablesByPeriodRef,
                  },
                  period,
                },
              }) => {
                if (!period) return;
                fetchLockedTablesByPeriodRef.send({
                  type: "fetch",
                  data: [period],
                });
                fetchBookedTablesByPeriodRef.send({
                  type: "fetch",
                  data: [period],
                });
              },
            },
          },
        },
      },
    },
    ui: {
      initial: "edit",
      states: {
        editLockedTables: {
          on: {
            validate: [
              {
                guard: ({ context: ctx }) =>
                  ctx.selectedLockedTables.length === 0,
                target: "edit",
              },
            ],
            selectTable: {
              actions: assign({
                selectedLockedTables: ({ context: ctx, event: ev }) => {
                  if (ctx.bookedTables.some((bt) => eq(bt, ev.data))) {
                    return [...ctx.selectedLockedTables];
                  }

                  if (
                    ctx.lockedTables.some((st) => eq(st, ev.data)) &&
                    !ctx.selectedLockedTables.some((st) => eq(st, ev.data))
                  ) {
                    return [...ctx.selectedLockedTables, ev.data];
                  }

                  return ctx.selectedLockedTables.filter(
                    (slt) => !eq(slt, ev.data),
                  );
                },
              }),
            },
          },
        },
        edit: {
          on: {
            validate: [
              {
                guard: ({ context: ctx }) =>
                  ctx.selectedLockedTables.length > 0,
                target: "editLockedTables",
              },
            ],
            selectTable: {
              actions: [
                assign(
                  ({
                    context: {
                      selectedTables,
                      lockedTables,
                      selectedLockedTables,
                      bookedTables,
                    },
                    event: ev,
                  }) => {
                    if (bookedTables.some((bt) => eq(bt, ev.data))) {
                      return {};
                    }

                    if (
                      selectedTables.length === 0 &&
                      lockedTables.some((st) => eq(st, ev.data)) &&
                      !selectedLockedTables.some((st) => eq(st, ev.data))
                    ) {
                      return {
                        selectedLockedTables: [
                          ...selectedLockedTables,
                          ev.data,
                        ],
                      };
                    }

                    if (selectedTables.some((st) => eq(st, ev.data))) {
                      return {
                        selectedTables: selectedTables.filter(
                          (st) => !eq(st, ev.data),
                        ),
                      };
                    }

                    if (lockedTables.some((lt) => eq(lt, ev.data))) {
                      return { selectedTables: [...selectedTables] };
                    }

                    return { selectedTables: [...selectedTables, ev.data] };
                  },
                ),
              ],
            },
          },
        },
      },
    },
  },
});
